import { Injectable, inject } from '@angular/core';
import interact from 'interactjs';
import { DragActiveEntryCSSClass } from '../../../../core/constants-repository/sortable-rows.constants';
import { TodoEntryModel } from '../../../../core/models/todo-planner/todo-entry.model';
import { SortableEntriesService } from '../../../../core/services/features/sortable-elements/sortable-entries.service';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';
import { CssAttributes } from '../../../calendar/components/time-block/rendering/css-attributes';
import { TodoEntryComponentHandlerService } from './todo-entry-component-handler.service';
import {
  TodoEntryCompletedCheck,
  TodoGhostEntryCSSClass,
} from '../../utils/todo-planner.constants';
import { TodoEntryDragService } from './drag-and-drop/todo-entry-drag.service';
import { assert } from 'src/app/core/assert/assert';
import { TodoEntryCrudService } from './todo-entry-crud.service';

@Injectable({
  providedIn: 'root',
})
export class TodoEntryInteractionService {
  // DI
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly todoEntryComponentHandlerService = inject(TodoEntryComponentHandlerService);
  private readonly todoEntryDragService = inject(TodoEntryDragService);
  private readonly sortableRowsService = inject(SortableEntriesService);
  private readonly todoEntryCrudService = inject(TodoEntryCrudService);

  /**
   * After a mouse click on the original component (i.e this to-do component) clone it and make it movable.
   * Then start the dragging automatically.
   * @param originalEl The original to-do component.
   * @param restrictionElement The boundaries for the dragged element.
   * @param todoEntryModel The model of the to-do entry.
   * @private
   */
  public handlePointerDown(
    originalEl: HTMLElement,
    restrictionElement: HTMLElement,
    todoEntryModel: TodoEntryModel,
  ): void {
    const i = interact(originalEl);
    // Avoid stacking multiple down handlers
    i.off('down');
    i.on('down', (downEvt) => {
      const src = downEvt.originalEvent?.target as HTMLElement | null;

      // Ignore click on "completed" checkbox.
      if (src?.closest(`.${TodoEntryCompletedCheck}`)) {
        return;
      }

      const interaction = downEvt.interaction;
      const interactable = downEvt.interactable;

      const startX = downEvt.clientX;
      const startY = downEvt.clientY;

      let started = false;

      const cleanup = () => {
        // IMPORTANT: detach from the SAME object you attached to
        interactable.off('move', moveListener);
        interactable.off('up', upListener);
        interactable.off('cancel', cancelListener);
      };

      const upListener = () => cleanup();
      const cancelListener = () => cleanup();

      const moveListener = (moveEvt: any) => {
        if (started) {
          return;
        }

        // If the user already released the pointer (common under load), abort.
        // (interactjs has a couple variants; these checks are safe)
        if (!interaction?.pointerIsDown) {
          cleanup();
          return;
        }

        const dx = Math.abs(moveEvt.clientX - startX);
        const dy = Math.abs(moveEvt.clientY - startY);

        if (dx <= 4 && dy <= 4) {
          return;
        }

        started = true;

        // Fetch a fresh copy of the to-do entry model here since the old model might be deprecated.
        const selectedModel = this.todoEntryCrudService.selectTodoEntry(todoEntryModel.id);
        const dragger = this.buildDraggableClone(originalEl, restrictionElement, selectedModel);

        // Start manual drag
        const cloneInteractable = interact(dragger);

        // If the interaction already ended between threshold and now, do nothing
        if (!interaction.pointerIsDown) {
          cleanup();
          return;
        }

        interaction.start({ name: 'drag' }, cloneInteractable, dragger);

        cleanup();
        downEvt.preventDefault();
      };

      interactable.on('move', moveListener);
      interactable.on('up', upListener);
      interactable.on('cancel', cancelListener);
    });
  }

  private buildDraggableClone(
    originalEl: HTMLElement,
    restrictionElement: HTMLElement,
    todoEntryModel: TodoEntryModel,
  ): HTMLElement {
    // Build the clone. Don't alter sorted index.
    const cloneRef = this.todoEntryComponentHandlerService.buildComponent(todoEntryModel);
    assert(cloneRef !== null);

    this.shiveRenderService.addClass(originalEl, TodoGhostEntryCSSClass);

    // Ensure clone is rendered before querying wrapper.
    cloneRef.changeDetectorRef.detectChanges();
    const originalRect = originalEl.getBoundingClientRect();
    const cloneWrapper = cloneRef.instance.todoEntryWrapper().nativeElement;

    // Make clone appear exactly where the original was clicked.
    this.shiveRenderService.setStyle(cloneWrapper, CssAttributes.position, 'fixed');
    this.shiveRenderService.setStyle(cloneWrapper, CssAttributes.left, `${originalRect.left}px`);
    this.shiveRenderService.setStyle(cloneWrapper, CssAttributes.top, `${originalRect.top}px`);
    this.shiveRenderService.setStyle(cloneWrapper, CssAttributes.width, `${originalRect.width}px`);
    this.shiveRenderService.setStyle(
      cloneWrapper,
      CssAttributes.height,
      `${originalRect.height}px`,
    );
    this.shiveRenderService.setStyle(cloneWrapper, CssAttributes.zIndex, '9999');

    // Attach classes
    this.shiveRenderService.addClass(cloneWrapper, DragActiveEntryCSSClass);

    // reset interact translation state
    this.shiveRenderService.setAttribute(cloneWrapper, 'data-x', '0');
    this.shiveRenderService.setAttribute(cloneWrapper, 'data-y', '0');
    this.shiveRenderService.setAttribute(cloneWrapper, CssAttributes.transform, 'translate(0, 0)');

    // Set sorted index
    const sortedIndex = this.sortableRowsService.getSortedIndex(originalEl);
    this.sortableRowsService.setSortedIndex(cloneWrapper, sortedIndex);

    // Init drag for clone (manualStart is already set in service)
    this.todoEntryDragService.initializeDragAndDrop(cloneRef, restrictionElement);

    return cloneWrapper;
  }
}
